import {
  Box,
  Button as MuiButton,
  Typography,
  ClickAwayListener,
  Avatar,
  styled,
} from '@mui/material'
import { colorPalette } from '../../../../style/colorPalette'
import { useCallback, useEffect, useRef, useState } from 'react'
import ClearRoundedIcon from '@mui/icons-material/ClearRounded'
import { Button } from '../../../../components/buttons'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import { SelectOption } from '../../../../model/selectOption'
import { ExpandLess, ExpandMore } from '@mui/icons-material'
import { intl } from '../../../../../i18n'
import {
  ProjectMemberPopper,
  ProjectMemberPopperHandle,
} from '../../../../features/ProjectMemberPopper'
import { ProjectMemberSearchOption } from '../../../../../domain/value-object/ProjectMemberSearchOption'
import { useProjectMemberRepository } from '../../../../../services/adaptors/projectMemberRepositoryAdaptor'

const Container = styled(Box)({
  border: `1px solid ${colorPalette.monotone[2]}`,
  borderRadius: '4px',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  padding: '5px 10px',
  height: '100%',
  outline: 'none',
})

const TitleTypography = styled(Typography)({
  color: colorPalette.monotone[4],
  minWidth: '80px',
  marginRight: '10px',
})

const PullDownSpan = styled('span')({
  display: 'flex',
  flexDirection: 'row',
  maxWidth: '416px',
})

const SelectedValueBox = styled(Box)({
  display: 'flex',
  padding: '3px 5px',
  borderRadius: '15px',
  margin: '3px 5px',
  flexWrap: 'wrap',
  background: colorPalette.monotone[1],
})

const SelectedAvatar = styled(Avatar)({
  width: 20,
  height: 20,
  margin: 'auto 5px auto 5px',
})

const SelectedNameTypography = styled(Typography)({
  margin: 'auto 5px',
  color: colorPalette.monotone[5],
})

const ClearButton = styled(Button)({
  border: 'none',
  background: colorPalette.monotone[0],
  width: '10px',
  height: '24px',
  borderRadius: '15px',
  cursor: 'pointer',
})

const StyledClearIcon = styled(ClearRoundedIcon)({
  color: colorPalette.monotone[4],
  height: '13px',
  width: '13px',
})

const AddButton = styled(Button)({
  borderRadius: '15px',
  border: `1px dashed ${colorPalette.monotone[2]}`,
  background: colorPalette.monotone[0],
  color: colorPalette.monotone[3],
  margin: '4px auto 4px 15px',
  cursor: 'pointer',
})

const StyledAddIcon = styled(AddRoundedIcon)({
  height: '12px',
  width: '12px',
})

const ExpandSpan = styled('span')({
  display: 'flex',
  flexDirection: 'row',
  maxWidth: '450px',
  margin: 'auto 0 auto auto',
})

const ExpandButton = styled(MuiButton)({
  minWidth: '20px',
  margin: '0 0 0 auto',
})

const StyledExpandLess = styled(ExpandLess)({
  color: colorPalette.monotone[4],
})

const StyledExpandMore = styled(ExpandMore)({
  color: colorPalette.monotone[4],
})

type ProjectMemberSearchConditionSelectBoxProps = {
  value: string | undefined
  fetchSelectedOption: (value: string) => Promise<SelectOption | undefined>
  title: string
  onChange: (newValue: string | undefined) => void
}

export const ProjectMemberSearchConditionSelectBox = ({
  value,
  fetchSelectedOption,
  title,
  onChange,
}: ProjectMemberSearchConditionSelectBoxProps) => {
  const [openPullDown, setOpenPullDown] = useState<boolean>(false)
  const [pullDownAnchorEl, setPullDownAnchorEl] = useState<Element>()
  const openPullDownRef = useRef<HTMLSpanElement>(null)
  const [openExpandIconPullDown, setOpenExpandIconPullDown] =
    useState<boolean>(false)

  const [searchText] = useState<string>('')
  const [memberOptions, setMemberOptions] = useState<
    ProjectMemberSearchOption[]
  >([])
  const popperRef = useRef<ProjectMemberPopperHandle>(null)
  const boxRef = useRef<HTMLDivElement>(null)
  const projectMemberRepository = useProjectMemberRepository()

  useEffect(() => {
    if (!openPullDown && !openExpandIconPullDown) return
    const searchMembers = async () => {
      const result =
        await projectMemberRepository.searchAsProjectMemberSearchOption(
          searchText
        )
      setMemberOptions(result)
    }
    searchMembers()
  }, [
    openPullDown,
    openExpandIconPullDown,
    searchText,
    projectMemberRepository,
  ])

  const [selectedOption, setSelectedOption] = useState<
    SelectOption | undefined
  >(undefined)
  useEffect(() => {
    if (!value) {
      setSelectedOption(undefined)
      return
    }
    fetchSelectedOption(value).then(option => {
      setSelectedOption(option)
    })
  }, [value, setSelectedOption, fetchSelectedOption])

  const handlePullDownClose = () => {
    setPullDownAnchorEl(undefined)
    setOpenPullDown(false)
  }

  const handleExpandIconPullDownClose = () => {
    setOpenExpandIconPullDown(false)
  }

  const clear = useCallback(() => {
    onChange('')
  }, [onChange])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (!openPullDown && !openExpandIconPullDown) return
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        popperRef.current?.arrowDown()
      } else if (event.key === 'ArrowUp') {
        event.preventDefault()
        popperRef.current?.arrowUp()
      } else if (event.key === 'Enter') {
        event.preventDefault()
        const selected = popperRef.current?.enter()
        if (selected) {
          setOpenPullDown(false)
          setPullDownAnchorEl(undefined)
          setOpenExpandIconPullDown(false)
        }
      }
    },
    [openPullDown, openExpandIconPullDown]
  )

  return (
    <Container ref={boxRef} tabIndex={0} onKeyDown={handleKeyDown}>
      <TitleTypography>{title}</TitleTypography>
      <ClickAwayListener onClickAway={handlePullDownClose}>
        <PullDownSpan ref={openPullDownRef}>
          {value && value.length >= 1 ? (
            <SelectedValueBox>
              <SelectedAvatar
                variant="circular"
                src={selectedOption?.iconUrl || undefined}
              />
              <SelectedNameTypography>
                {selectedOption?.name || null}
              </SelectedNameTypography>
              <ClearButton colorPattern="monotone" onClick={clear}>
                <StyledClearIcon />
              </ClearButton>
            </SelectedValueBox>
          ) : (
            <Box>
              <AddButton
                colorPattern="monotone"
                size="s"
                onClick={async () => {
                  if (openPullDown) {
                    setPullDownAnchorEl(undefined)
                    setOpenPullDown(false)
                  } else {
                    const result =
                      await projectMemberRepository.searchAsProjectMemberSearchOption(
                        searchText
                      )
                    setMemberOptions(result)
                    setPullDownAnchorEl(openPullDownRef.current || undefined)
                    setOpenPullDown(true)
                    boxRef.current?.focus()
                  }
                }}
              >
                <Typography>
                  {intl.formatMessage({
                    id: 'wbsItemSearchFilterPanel.click.select',
                  })}
                </Typography>
                <StyledAddIcon />
              </AddButton>
            </Box>
          )}
        </PullDownSpan>
      </ClickAwayListener>
      {openPullDown && (
        <ProjectMemberPopper
          ref={popperRef}
          open={openPullDown}
          anchorEl={pullDownAnchorEl as HTMLElement}
          options={memberOptions}
          onSelect={(member: ProjectMemberSearchOption) => {
            onChange(member.uuid)
            setOpenPullDown(false)
            setPullDownAnchorEl(undefined)
          }}
          inputRef={boxRef as React.RefObject<HTMLInputElement>}
          searchText={searchText}
        />
      )}
      <ClickAwayListener onClickAway={handleExpandIconPullDownClose}>
        <ExpandSpan>
          <ExpandButton
            onClick={async () => {
              if (openExpandIconPullDown) {
                setOpenExpandIconPullDown(false)
              } else {
                const result =
                  await projectMemberRepository.searchAsProjectMemberSearchOption(
                    searchText
                  )
                setMemberOptions(result)
                setOpenExpandIconPullDown(true)
                boxRef.current?.focus()
              }
            }}
          >
            {openExpandIconPullDown ? (
              <StyledExpandLess />
            ) : (
              <StyledExpandMore />
            )}
          </ExpandButton>
        </ExpandSpan>
      </ClickAwayListener>
      {openExpandIconPullDown && (
        <ProjectMemberPopper
          ref={popperRef}
          open={openExpandIconPullDown}
          anchorEl={openPullDownRef.current as HTMLElement}
          options={memberOptions}
          onSelect={(member: ProjectMemberSearchOption) => {
            onChange(member.uuid)
            setOpenExpandIconPullDown(false)
          }}
          inputRef={boxRef as React.RefObject<HTMLInputElement>}
          searchText={searchText}
        />
      )}
    </Container>
  )
}
